import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import utils from "./../../universal/utils";
import PushNotificationOptIn from "../account/PushNotificationOptIn";

const ACCOUNT_PATH = "/account";
export const PLAYSAFE_READY_NOTICE_STORAGE_KEY = "ouro_playsafe_ready_to_queue_seen";

function isSteamSignupUser(user) {
  if (user?.signupSource === "steam") return true;
  const email = String(user?.email || "");
  return email.endsWith("@steam.ouro.local");
}

export function isPlaySafeSignupUser(user) {
  if (user?.signupSource === "playsafe" || user?.playsafeId) return true;
  const email = String(user?.email || "");
  return email.endsWith("@playsafe.ouro.local");
}

/**
 * One-time lobby notice for verified PlaySafe users (dismiss stored in localStorage).
 */
export function getPlaySafeReadyNotice(user) {
  if (!isPlaySafeSignupUser(user)) return null;
  if (user?.accountStatus !== "verified") return null;

  return {
    variant: "card",
    severity: "success",
    title: "You're ready to queue",
    body:
      "You signed in with PlaySafe, so your identity is already verified and you can join matchmaking right away. Optionally complete your profile on the account page to strengthen your trust score.",
    ctaLabel: "COMPLETE PROFILE",
    dismissLabel: "GOT IT",
    localStorageKey: PLAYSAFE_READY_NOTICE_STORAGE_KEY,
    fetchReadiness: false,
  };
}

function useNoticeDismissed(storageKey) {
  const [dismissed, setDismissed] = useState(() => {
    if (!storageKey || typeof window === "undefined" || !window.localStorage) {
      return false;
    }
    try {
      return window.localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  const dismiss = useCallback(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, "1");
    } catch {
      // localStorage may be unavailable
    }
    setDismissed(true);
  }, [storageKey]);

  return { dismissed, dismiss };
}

function ProviderNoticeCard({ notice, onDismiss, user }) {
  const accentColor = notice.severity || "info";

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 2,
        mb: 1,
        borderLeftWidth: 4,
        borderLeftStyle: "solid",
        borderLeftColor:
          theme.palette[accentColor]?.main ?? theme.palette.info.main,
      })}
    >
      <Typography
        component="h2"
        variant="subtitle1"
        fontWeight={600}
        gutterBottom
      >
        {notice.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {notice.body}
      </Typography>
      {notice.showPushOptIn && (
        <Box sx={{ mb: 2 }}>
          <PushNotificationOptIn user={user} compact />
        </Box>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {notice.ctaLabel && (
          <Button
            component={Link}
            to={notice.ctaHref || ACCOUNT_PATH}
            variant="contained"
            color={accentColor}
            size="small"
          >
            {notice.ctaLabel}
          </Button>
        )}
        {notice.dismissLabel && onDismiss && (
          <Button variant="outlined" color={accentColor} size="small" onClick={onDismiss}>
            {notice.dismissLabel}
          </Button>
        )}
      </Box>
    </Paper>
  );
}

/**
 * Returns a notice config for Steam users who have not yet supplied their
 * real email or legal name. This is checked before the generic accountStatus
 * check so Steam users see a targeted, actionable prompt rather than the
 * generic "complete verification" copy.
 */
export function getSteamCompletionNotice(user) {
  if (!isSteamSignupUser(user)) return null;
  if (!user?.emailIsSynthetic || user?.emailVerifiedAt) return null;

  const hasName = !!(user?.name?.trim());
  const title = "Application pending review";
  const body = hasName
    ? "Your Steam sign-in is enough for our team to review your application. Add a verified email or enable browser alerts so we can update you, and keep improving your profile to increase your trust factor."
    : "Your Steam sign-in is enough for our team to review your application. Add a verified email or enable browser alerts so we can update you, and add more profile details if you want to increase your trust factor.";

  return {
    variant: "card",
    severity: "info",
    title,
    body,
    ctaLabel: "ADD EMAIL OR PROFILE DETAILS",
    showPushOptIn: true,
    fetchReadiness: false,
  };
}

export function getLobbyAccountStatusNotice(accountStatus, options = {}) {
  const { missingItems = null, phoneVerificationStatus = null } = options;

  if (accountStatus === "pending_steam_link") {
    return {
      variant: "card",
      severity: "warning",
      title: "Connect your Steam account to continue",
      body:
        "PlaySafe ID needs your Steam account linked before we can confirm your identity and enable matchmaking.",
      ctaLabel: "CONNECT STEAM",
      ctaHref: "/auth/playsafe/connect-steam?steam_link=session",
      fetchReadiness: false,
    };
  }
  if (accountStatus === "pending") {
    return {
      variant: "card",
      severity: "info",
      title: "Complete your verification to unlock matchmaking",
      body:
        "Welcome to Ouro. Finish your account setup so our team can review your profile and enable matchmaking access. If you have a FaceIT profile, link it during setup so your ELO carries over and you keep playing at your level from day one.",
      ctaLabel: "COMPLETE ACCOUNT SETUP",
      fetchReadiness: true,
    };
  }
  if (accountStatus === "pending_review") {
    if (phoneVerificationStatus === "phone_verification_ready") {
      return {
        variant: "card",
        severity: "success",
        title: "Optional: verify your phone number",
        body:
          "Your profile is under review. Phone verification is optional, but completing it can increase your trust factor and help reviewers build confidence faster.",
        ctaLabel: "VERIFY PHONE NOW",
        fetchReadiness: true,
      };
    }

    if (phoneVerificationStatus === "code_sent") {
      return {
        variant: "card",
        severity: "info",
        title: "Enter your SMS verification code",
        body:
          "We sent a one-time code to your phone. Open your account page and enter it to complete optional phone verification.",
        ctaLabel: "ENTER CODE ON ACCOUNT",
        fetchReadiness: true,
      };
    }

    if (phoneVerificationStatus === "expired") {
      return {
        variant: "card",
        severity: "warning",
        title: "Your SMS code expired",
        body:
          "The verification code is no longer valid. Head to your account settings and click \"Send Verification Code\" to request a new one.",
        ctaLabel: "RESEND CODE ON ACCOUNT",
        fetchReadiness: true,
      };
    }

    return {
      variant: "card",
      severity: "info",
      title: "Profile under review",
      body:
        "Your profile is under review. Most players are verified within a few hours — we'll send you an update when done.",
      ctaLabel: null,
      fetchReadiness: true,
    };
  }
  if (accountStatus === "rejected") {
    return {
      variant: "alert",
      severity: "warning",
      title: "Your verification needs attention",
      body:
        "Our review team could not approve your current details. Review your account settings for the latest guidance and make sure your Steam profile and inventory are visible before submitting again.",
      ctaLabel: "REVIEW ACCOUNT SETTINGS",
    };
  }
  return null;
}

function PendingNoticeWithReadiness({
  notice,
  readinessRefreshKey,
  phoneVerificationStatus,
}) {
  const [missingItems, setMissingItems] = useState(null);

  useEffect(() => {
    let cancelled = false;

    setMissingItems(null);

    async function fetchReadiness() {
      try {
        const basePath = utils.getEdgeBasePath();
        const res = await fetch(`${basePath}/api/v1/account/readiness`, {
          credentials: "include",
        });
        const data = await res.json();
        if (!cancelled && data?.ok && data.readiness) {
          setMissingItems(data.readiness.missingItems || []);
        }
      } catch (_) {
        // silently fall back to generic message
      }
    }
    fetchReadiness();
    return () => { cancelled = true; };
  }, [notice.accountStatus, readinessRefreshKey, phoneVerificationStatus]);

  const resolvedNotice = getLobbyAccountStatusNotice(notice.accountStatus, {
    missingItems,
    phoneVerificationStatus,
  });
  const stepsAway = missingItems ? missingItems.length : null;
  const showPendingChecklist =
    notice.accountStatus === "pending" && stepsAway != null && stepsAway > 0;
  const bodyText = showPendingChecklist
    ? `You're ${stepsAway} step${stepsAway === 1 ? "" : "s"} away from matchmaking: ${missingItems.join(", ")}`
    : resolvedNotice.body;

  const accentColor = resolvedNotice.severity || "info";

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 2,
        mb: 1,
        borderLeftWidth: 4,
        borderLeftStyle: "solid",
        borderLeftColor: theme.palette[accentColor]?.main ?? theme.palette.info.main,
      })}
    >
      <Typography
        component="h2"
        variant="subtitle1"
        fontWeight={600}
        gutterBottom
      >
        {resolvedNotice.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {bodyText}
      </Typography>
      {showPendingChecklist && (
        <Box sx={{ mb: 1.5 }}>
          {missingItems.map((item) => (
            <Typography key={item} variant="body2" sx={{ pl: 1, color: 'warning.main' }}>
              • {item}
            </Typography>
          ))}
        </Box>
      )}
      {resolvedNotice.ctaLabel && (
        <Button
          component={Link}
          to={resolvedNotice.ctaHref || ACCOUNT_PATH}
          variant="contained"
          color={accentColor}
          size="small"
        >
          {resolvedNotice.ctaLabel}
        </Button>
      )}
    </Paper>
  );
}

export function LobbyAccountStatusNotice({
  accountStatus,
  readinessRefreshKey,
  phoneVerificationStatus = null,
  user = null,
}) {
  const playSafeReady = getPlaySafeReadyNotice(user);
  const { dismissed: playSafeDismissed, dismiss: dismissPlaySafeNotice } =
    useNoticeDismissed(playSafeReady?.localStorageKey);

  // Provider-specific prompts (PlaySafe / Steam) take priority over generic status copy.
  const providerNotice =
    (playSafeReady && !playSafeDismissed ? playSafeReady : null) ||
    getSteamCompletionNotice(user);
  const notice = providerNotice || getLobbyAccountStatusNotice(accountStatus, {
    phoneVerificationStatus,
  });
  if (!notice) {
    return null;
  }
  if (notice.fetchReadiness && notice.variant === "card") {
    return (
      <PendingNoticeWithReadiness
        notice={{ ...notice, accountStatus }}
        readinessRefreshKey={readinessRefreshKey}
        phoneVerificationStatus={phoneVerificationStatus}
      />
    );
  }
  if (notice.variant === "card") {
    return (
      <ProviderNoticeCard
        notice={notice}
        onDismiss={notice.dismissLabel ? dismissPlaySafeNotice : undefined}
        user={user}
      />
    );
  }
  return (
    <Alert
      severity={notice.severity}
      sx={{ mb: 1 }}
      action={
        notice.ctaLabel ? (
          <Button
            color="inherit"
            size="small"
            component={Link}
            to={notice.ctaHref || ACCOUNT_PATH}
            sx={{ minWidth: 180 }}
          >
            {notice.ctaLabel}
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{notice.title}</AlertTitle>
      {notice.body}
    </Alert>
  );
}